// ─── Patrón para ActaInicialPDF.jsx y ActaInicialPDF.jsx ───────────────────────
// Usa @react-pdf/renderer para preview Y descarga desde el mismo Document.
// ─────────────────────────────────────────────────────────────────────────────

import { forwardRef, useImperativeHandle, useEffect, useState } from "react";

import { 
  Document, 
  Page, 
  Text, 
  View, 
  StyleSheet, 
  PDFViewer,
  pdf,
  Image
} from '@react-pdf/renderer';

import config from "../../../../config";
import { getAllProyectoByid } from "../../../../services/sostenibilidad/formatos/sostenibilidadFormatos";
import { message } from "antd";
import dayjs from "dayjs";

const baseUrl = config.baseUrl;

// ─────────────────────────────────────────────
//  PALETA DE COLORES
// ─────────────────────────────────────────────
const COLOR_PRIMARY   = "#355F91"; // azul institucional
const COLOR_HEADER_BG = "#355F91";
const COLOR_HEADER_FG = "#FFFFFF";
const COLOR_GRAY_BG   = "#F5F5F5";
const COLOR_GRAY_MID  = "#BFBFBF";
const COLOR_BORDER    = "#CCCCCC";
const COLOR_TEXT      = "#1A1A1A";
const COLOR_MUTED     = "#555555";
const COLOR_FOOTER_BG = "#F0F4FA";


// Estilos para el PDF
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    paddingTop: 10,
    paddingRight: 50,
    paddingLeft: 50,
    paddingBottom: 68,   /* igual a la height fija del footer — nunca cambia */
    fontFamily: "Helvetica",
  },
  section: {
    margin: 5,
    padding: 5,
  },
  title: {
    fontSize: 12,
    marginTop: 5,
    marginBottom: 20,
    textAlign: "center",
    fontWeight: "bold",
    fontFamily: "Helvetica-Bold",
  },
  title_1: {
    fontSize: 13,
    textAlign: "left",
    fontWeight: "800",
    marginTop: 2,
  },


  headerTable: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    width: "100%",
    paddingTop: 10,
    paddingRight: 10,
    paddingLeft: 10,
    marginBottom: 20,
  },
  banner: {
    width: "100%",
    height: 80,
  },
  table: {
    width: "100%",
    borderWidth: 1,
    borderColor: "#000",
    borderStyle: "solid",
    marginTop: 10,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
  },
  tableRowLast: {
    flexDirection: "row",
  },

  logoColumn: {
    width: "100%",
    alignItems: "center",
  },


  text: {
    fontSize: 9,
    fontFamily: "Helvetica",
    textAlign: "left",
  },

  tableCol: {
    width: "33%", // Cada columna ocupa un tercio del ancho
    padding: 5,
    textAlign: "center",
    borderLeftWidth: 1,
    borderRightWidth: 1,
  },
  tableCell: {
    fontSize: 10,
    padding: 5,
  },
  questionSection: {
    marginTop: 7,
  },
  questionText: {
    fontSize: 12,
    fontWeight: "bold",
    fontFamily: "Helvetica-Bold",
    marginBottom: 5,
  },
  checkbox: {
    width: 15,
    height: 15,
    borderWidth: 1,
    borderColor: "#000",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 10, // Añadir separación entre la casilla y el texto
  },
  checkboxText: {
    fontSize: 10,
    color: "#000",
  },
  checkboxContainerHorizontal: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    marginTop: 7,
  },
  checkboxContainer: {
    flexDirection: "row",
    marginBottom: 10,
  },
  checked: {
    backgroundColor: "#fff",
    justifyContent: "center",
    marginR: 10,
    alignItems: "center",
    borderColor: "#000",
  },
  textarea: {
    fontSize: 10,
    borderWidth: 1,
    borderColor: "#000",
    padding: 10,
    borderRadius: 5,
    width: "100%", // Ajuste automático al ancho disponible
    minHeight: 40, // Asegura que tenga un mínimo de altura
    flexShrink: 1, // Permite que el contenedor ajuste su tamaño según el contenido
    backgroundColor: "#f0f0f0",
  },
  signatureContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 20,
    marginBottom: 20, // Agregar separación al final de la sección
  },
  signature: {
    width: 150,
    height: 50,
    marginBottom: 10,
  },
  signatureLine: {
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    marginTop: 10,
  },
  interlineText: {
    marginTop: 15,
    fontSize: 10,
    textAlign: "center",
  },
  footerSpacer: {
    flexGrow: 1, // Esto empuja el contenido hacia arriba
  },
  column: {
    width: "33%", // Cada columna ocupa un tercio del ancho
    alignItems: "center", // Centra los elementos dentro de las columnas
  },
  tableCol30: {
    width: "30%",
    padding: 5,
    borderRightWidth: 1,
    borderRightColor: "#000",
    justifyContent: "center",
  },
  tableCol70: {
    width: "70%",
    padding: 5,
    justifyContent: "center",
  },
  sectionTitle: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    marginTop: 12,
    marginBottom: 8,
    textAlign: "center",
  },
  normalText: {
    fontSize: 10,
    fontFamily: "Helvetica",
  },
  normalTextTitle: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
  },

  paragraph: {
    fontSize: 10,
    textAlign: "justify",
    marginBottom: 6,
    lineHeight: 1.5,
  },


  tableCol100: {
    width: "100%",
    padding: 5,
  },
  subtitleRow: {
    backgroundColor: "#f2f2f2",
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#000",
  },

  signatureBlock: {
    marginTop: 16,
    marginBottom: 10,
    width: "100%",
  },
  signatureHeader: {
    fontSize: 10,
    fontWeight: "bold",
    fontFamily: "Helvetica-Bold",
    marginBottom: 8,
  },
  signatureContent: {
    marginLeft: 20,
    marginTop: 6,
  },
  signatureLine: {
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    width: "60%",
    marginBottom: 5,
  },

  signatureText: {
    fontSize: 10,
    marginBottom: 3,
    minHeight: 14,
  },
  signatureImage: {
    width: 130,
    height: 70,
    marginBottom: 5,
  },
  // ────────────────────────────────────────────
  //  FOOTER FIJO — posición absoluta, NO genera página extra
  // ────────────────────────────────────────────
  pageFooter: {
    position: "absolute",
    bottom: 20,
    left: 50,
    right: 50,
    borderTopWidth: 0.5,
    borderTopColor: COLOR_GRAY_MID,
    paddingTop: 5,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  footerLegalText: {
    fontSize: 6.5,
    fontFamily: "Helvetica",
    color: COLOR_MUTED,
    flex: 1,
    lineHeight: 1.4,
    paddingRight: 10,
  },
  footerPageNum: {
    fontSize: 7.5,
    fontFamily: "Helvetica-Bold",
    color: COLOR_MUTED,
    textAlign: "right",
    minWidth: 50,
  },
  footerCode: {
    fontSize: 8,
    fontFamily: "Helvetica",
    fontStyle: "italic",
    color: COLOR_MUTED,
    position: "absolute",
    bottom: 55,
    left: 50,
  },

   // ────────────────────────────────────────────
  //  HEADER INSTITUCIONAL (3 columnas igual que el PDF de referencia)
  //  [LOGO ARN]  |  ACTA DE VISITA ASISTENCIA TÉCNICA  |  [CODIGO / FECHA / VERSION]
  // ────────────────────────────────────────────
  headerWrapper: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: COLOR_TEXT,
    marginBottom: 10,
  },
  headerLogo: {
    width: "22%",
    borderRightWidth: 1,
    borderRightColor: COLOR_TEXT,
    padding: 6,
    justifyContent: "center",
    alignItems: "center",
  },
  headerLogoImg: {
    width: "100%",
    height: 52,
    objectFit: "contain",
  },
  headerTitle: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 6,
    borderRightWidth: 1,
    borderRightColor: COLOR_TEXT,
  },
  headerTitleText: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
    color: COLOR_TEXT,
  },
  headerMeta: {
    width: "26%",
    flexDirection: "column",
  },
  headerMetaRow: {
    flex: 1,
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: COLOR_TEXT,
    paddingHorizontal: 6,
    paddingVertical: 3,
  alignItems: "center",
  justifyContent: "center", 
  },

  headerMetaRowLast: {
    flex: 1,
    flexDirection: "row",
    paddingHorizontal: 6,
    paddingVertical: 3,
    alignItems: "center",
    justifyContent:"center"
  },
  headerMetaLabel: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: COLOR_TEXT,
    marginRight: 4,
  },
  headerMetaValue: {
    fontSize: 8,
    fontFamily: "Helvetica",
    color: COLOR_TEXT,
  },
  headerMetaVersionRow: {
    flex: 1,
    flexDirection: "row",
  },
  headerMetaVersionCell: {
    flex: 1,
    paddingHorizontal: 6,
    paddingVertical: 3,
    alignItems: "center",
    justifyContent: "center",
  },
  headerMetaVersionDivider: {
    borderLeftWidth: 1,
    borderLeftColor: COLOR_TEXT,
  },

});


// Componente del documento PDF
const ActaFinalDocumento = ({ formData }) => {

    if (!formData) return null;

    const comites = formData?.comites || [];

const formatPeso = (value) => {
  if (!value && value !== 0) return "";
  const num = parseFloat(String(value).replace(/[^0-9.-]/g, ""));
  if (isNaN(num)) return String(value);
  return "$ " + num.toLocaleString("es-CO", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const construirNombramiento = () => {
  const tipo = formData?.tipo_nombramiento;

  if (!tipo || tipo === "No aplica") {
    return "nombrado mediante (No aplica)";
  }

  const tipoFinal =
    tipo === "Otro"
      ? formData?.otro_tipo_nombramiento
      : tipo;

  const numero = formData?.numero_nombramiento;
  const fechaNom = formData?.fecha_nombramiento;
  const fechaPos = formData?.fecha_posesion;

  let texto = `nombrado mediante ${tipoFinal}`;

  if (numero) {
    texto += ` No. ${numero}`;
  }

  if (fechaNom) {
    texto += ` del ${dayjs(fechaNom).format("DD/MM/YYYY")}`;
  }

  if (fechaPos) {
    texto += `, con fecha de posesión ${dayjs(fechaPos).format("DD/MM/YYYY")}`;
  }

  return texto;
};

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* ══════════════════════════════════════
                     HEADER INSTITUCIONAL — se repite en cada página
                     [LOGO]  |  ACTA DE VISITA  |  CODIGO/FECHA/VERSION
                 ══════════════════════════════════════ */}
                 <View style={styles.headerWrapper} fixed>
                   {/* Logo ARN */}
                   <View style={styles.headerLogo}>
                     <Image style={styles.headerLogoImg} src={"Logo_ARN.png"} />
                   </View>
         
                   {/* Título central */}
                   <View style={styles.headerTitle}>
                     <Text style={styles.headerTitleText}>ACTA DE FINALIZACIÓN DE ASISTENCIA TÉCNICA A PROYECTOS PRODUCTIVOS EN REINCORPORACIÓN</Text>
                   </View>
         
                   {/* Código / Fecha / Versión */}
                   <View style={styles.headerMeta}>
                     {/* Fila CÓDIGO */}
                     <View style={styles.headerMetaRow}>
                       <Text style={styles.headerMetaLabel}>CODIGO :</Text>
                       <Text style={styles.headerMetaValue}>IR-F-125</Text>
                     </View>
                     {/* Fila FECHA + VERSION */}
                     <View style={styles.headerMetaVersionRow}>
                       <View style={styles.headerMetaVersionCell}>
                         <Text style={styles.headerMetaLabel}>FECHA :</Text>
                         <Text style={styles.headerMetaValue}>2021-08-10</Text>
                       </View>
                       <View style={[styles.headerMetaVersionCell, styles.headerMetaVersionDivider]}>
                         <Text style={styles.headerMetaLabel}>VERSION :</Text>
                         <Text style={styles.headerMetaValue}>V-1</Text>
                       </View>
                     </View>
                   </View>
                 </View>


        {/* Agregar contenido del acta */}
        <View style={styles.section}>
          <Text style={styles.normalText}>
            Fecha del acta: {formData?.dia || ""}
            {"/"}
            {formData?.mes || ""}
            {"/"}
            {formData?.vigencia || ""}
          </Text>

          {/* Título principal con numeración */}
          <Text style={styles.sectionTitle}>
            1. IDENTIFICACIÓN DEL PROYECTO PRODUCTIVO
          </Text>

          <View style={styles.table}>
            {/* Primera parte de la tabla */}
            <View style={styles.tableRow}>
              <View style={styles.tableCol30}>
                <Text style={styles.normalTextTitle}>
                  Nombre del proyecto productivo:
                </Text>
              </View>
              <View style={styles.tableCol70}>
                <Text style={styles.normalText}>
                  {formData?.nombre_proyecto || ""}
                </Text>
              </View>
            </View>

            <View style={styles.tableRow}>
              <View style={styles.tableCol30}>
                <Text style={styles.normalTextTitle}>
                  Nombre de la forma Asociativa:
                </Text>
              </View>
              <View style={styles.tableCol70}>
                <Text style={styles.normalText}>
                  {formData?.organizacion || ""}
                </Text>
              </View>
            </View>

            <View style={styles.tableRow}>
              <View style={styles.tableCol30}>
                <Text style={styles.normalTextTitle}>
                  Dependencia de la ARN:
                </Text>
              </View>
              <View style={styles.tableCol70}>
                <Text style={styles.normalText}>
                  {formData?.grupo_territorial || ""}
                </Text>
              </View>
            </View>


            <View style={styles.tableRow}>
              <View style={styles.tableCol30}>
                <Text style={styles.normalTextTitle}>
                  Código del proyecto PRE-PP:
                </Text>
              </View>
              <View style={styles.tableCol70}>
                <Text style={styles.normalText}>
                  {formData?.codigo_pre || ""}
                </Text>
              </View>
            </View>

            <View style={styles.tableRow}>
              <View style={styles.tableCol30}>
                <Text style={styles.normalTextTitle}>
                  Número de personas en reincorporación vinculadas al proyecto
                  productivo
                </Text>
              </View>
              <View style={styles.tableCol70}>
                <Text style={styles.normalText}>
                  {formData?.total_participantes || ""}
                </Text>
              </View>
            </View>

            {/* Subtítulo de Ubicación */}
            <View style={styles.tableRow}>
              <View style={[styles.tableCol100, styles.subtitleRow]}>
                <Text style={styles.sectionTitle}>Ubicación del proyecto</Text>
              </View>
            </View>

            <View style={styles.tableRow}>
              <View style={styles.tableCol30}>
                <Text style={styles.normalTextTitle}>Departamento:</Text>
              </View>
              <View style={styles.tableCol70}>
                <Text style={styles.normalText}>
                  {formData?.departamento || ""}
                </Text>
              </View>
            </View>

            <View style={styles.tableRow}>
              <View style={styles.tableCol30}>
                <Text style={styles.normalTextTitle}>Municipio:</Text>
              </View>
              <View style={styles.tableCol70}>
                <Text style={styles.normalText}>
                  {formData?.municipio || ""}
                </Text>
              </View>
            </View>

            <View style={styles.tableRow}>
              <View style={styles.tableCol30}>
                <Text style={styles.normalTextTitle}>Vereda:</Text>
              </View>
              <View style={styles.tableCol70}>
                <Text style={styles.normalText}>
                  {formData?.ubicacion_proyecto || ""}
                </Text>
              </View>
            </View>

            <View style={styles.tableRowLast}>
              <View style={styles.tableCol30}>
                <Text style={styles.normalTextTitle}>
                  Acompañamiento y asistencia técnica por:
                </Text>
              </View>
              <View style={styles.tableCol70}>
                <Text style={styles.normalText}>
                  {formData?.nombre_organizacion || ""}
                </Text>
              </View>
            </View>
          </View>

          {/* Título principal con numeración */}
          <Text style={styles.sectionTitle}>
            2. INFORMACIÓN FINANCIERA DEL PROYECTO PRODUCTIVO
          </Text>

          <View style={styles.table}>
            {/* Primera parte de la tabla */}
            <View style={styles.tableRow}>
              <View style={styles.tableCol30}>
                <Text style={styles.normalTextTitle}>
                  Valor del total del proyecto:
                </Text>
              </View>
              <View style={styles.tableCol70}>
                <Text style={styles.normalText}>
                  {formatPeso(formData?.valor_total_proyecto)}
                </Text>
              </View>
            </View>


                        <View style={styles.tableRow}>
              <View style={styles.tableCol30}>
                <Text style={styles.normalTextTitle}>
                  Valor del apoyo económico de reincorporación
                </Text>
              </View>
              <View style={styles.tableCol70}>
                <Text style={styles.normalText}>
                  {formatPeso(formData?.valor_apoyo_economico_reincorporacion)}
                </Text>
              </View>
            </View>


            
            <View style={styles.tableRow}>
              <View style={styles.tableCol30}>
                <Text style={styles.normalTextTitle}>
                  Valor de los aportes propios
                </Text>
              </View>
              <View style={styles.tableCol70}>
                <Text style={styles.normalText}>
                  {formatPeso(formData?.valor_aportes_propios)}
                </Text>
              </View>
            </View>


            <View style={styles.tableRow}>
              <View style={styles.tableCol30}>
                <Text style={styles.normalTextTitle}>
                  Valor cierre financiero
                </Text>
              </View>
              <View style={styles.tableCol70}>
                <Text style={styles.normalText}>
                  {formatPeso(formData?.valor_cierre_financiero)}
                </Text>
              </View>
            </View>


            <View style={styles.tableRowLast}>
              <View style={styles.tableCol30}>
                <Text style={styles.normalTextTitle}>
                  Valor otros aportes
                </Text>
              </View>
              <View style={styles.tableCol70}>
                <Text style={styles.normalText}>
                  {formatPeso(formData?.valor_otros_aportes)}
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* Sección para proyectos colectivos */}
        <View style={styles.section}>
          <Text style={styles.paragraph}>

            {formData?.name_organizacion_sigla || "<< Se debe diligenciar el nombre de la organización – Sigla>>"}, con número de NIT: {""}
            {formData?.nit_organizacion || "<< Registrar número de NIT de la organización>>"}, representado por {formData?.representante_legal_organizacion || "<<nombre del representante legal de la organización >>"} {" "}
            identificado con la cédula de ciudadanía No. {formData?.cedula_representante_legal_organizacion || "<<número de cédula >>"} {""}
            obrando en calidad de  representante legal ,{construirNombramiento()}, {" "}
            debidamente facultado y la Agencia para la Reincorporación y la Normalización - ARN, representado por{" "}
            {formData?.nombre_coordinador_grupo_territorial  || "<< escribir el nombre del Coordinador del Grupo Territorial y/o delegado >>"}, como delegado por la {""}
            {formData?.grupo_territorial || "<<nombre del Grupo Territorial>>"}, dejan constancia{" "}
            de los siguientes aspectos:
          </Text>
        </View>


         {/* Sección de Constancia */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Constancia</Text>

       <Text style={styles.paragraph}>
  <Text style={{ fontFamily: "Helvetica-Bold" }}>Primero: </Text>
  Del cierre formal al acompañamiento y asistencia técnica al proyecto
  productivo de la referencia, en cumplimiento de lo establecido en el marco
  de la Resolución 4309 de 2019, Titulo II, Artículo 12.
</Text>

<Text style={styles.paragraph}>
  <Text style={{ fontFamily: "Helvetica-Bold" }}>Segundo: </Text>
  Las partes aquí descritas acuerdan que la Agencia para la Reincorporación
  y la Normalización - (ARN) está exenta de responsabilidad en toda controversia
  derivada del proyecto productivo escogido, entendiendo que dichos recursos serán
  administrados por la(s) persona(s) en reincorporación relacionada(s) en el presente documento.
</Text>
        </View>


     {/* Párrafo de cierre y firmas */}
        <View style={styles.section}>
          <Text style={styles.paragraph}>
            Conforme lo anterior se suscribe la presente acta el día{" "}
            {formData.dia_firma || "     "} del mes {formData.mes_firma || "                "} del año{" "}
            {formData.vigencia_firma || "     "}, en la ciudad de {formData.ciudad_firma || "                "}.
          </Text>
          </View>


 {/* Sección de Firmas */}
        <View style={styles.section}>
          {/* Firma ARN Grupo Territorial */}
          <View style={styles.signatureBlock}>
            <Text style={styles.signatureHeader}>Por ARN Grupo Territorial</Text>
            <View style={styles.signatureContent}>
              <View style={{ height: 30 }} />
              <View style={{ flexDirection: "row", alignItems: "flex-end", marginBottom: 5 }}>
                <Text style={[styles.signatureText, { marginRight: 6, marginBottom: 0 }]}>Firma:</Text>
                <View style={{ flex: 1, borderBottomWidth: 1, borderBottomColor: "#000" }} />
              </View>
              <Text style={styles.signatureText}>Nombre:</Text>
              <Text style={styles.signatureText}>No. Cédula:</Text>
              <Text style={styles.signatureText}>Cargo:</Text>
            </View>
          </View>

          {/* Firma Por la(s) persona(s) en reincorporación */}
          <View style={styles.signatureBlock}>
            <Text style={styles.signatureHeader}>Por la(s) persona(s) en reincorporación</Text>
            <View style={styles.signatureContent}>
              <View style={{ height: 30 }} />
              <View style={{ flexDirection: "row", alignItems: "flex-end", marginBottom: 5 }}>
                <Text style={[styles.signatureText, { marginRight: 6, marginBottom: 0 }]}>Firma:</Text>
                <View style={{ flex: 1, borderBottomWidth: 1, borderBottomColor: "#000" }} />
              </View>
              <Text style={styles.signatureText}>Nombre:</Text>
              <Text style={styles.signatureText}>Cargo:</Text>
            </View>
          </View>

          {/* Firma Por parte de la Organización del acompañamiento y la asistencia técnica */}
          <View style={styles.signatureBlock}>
            <Text style={styles.signatureHeader}>Por parte de la Organización del acompañamiento y la asistencia técnica</Text>
            <View style={styles.signatureContent}>
              <View style={{ height: 30 }} />
              <View style={{ flexDirection: "row", alignItems: "flex-end", marginBottom: 5 }}>
                <Text style={[styles.signatureText, { marginRight: 6, marginBottom: 0 }]}>Firma:</Text>
                <View style={{ flex: 1, borderBottomWidth: 1, borderBottomColor: "#000" }} />
              </View>
              <Text style={styles.signatureText}>Nombre:</Text>
              <Text style={styles.signatureText}>Cargo:</Text>
            </View>
          </View>
        </View>

        {/* ══════════════════════════════════════
            FOOTER LEGAL FIJO — aparece en todas las páginas
            Incluye Código Único solo en la última página
        ══════════════════════════════════════ */}
        {/* ══ FOOTER FIJO — altura constante en TODAS las páginas ══
            El bloque siempre mide lo mismo: reserva espacio para el
            Código Único en todas las páginas aunque esté vacío.
            Esto evita cualquier recálculo de layout que genere página extra.
        ═════════════════════════════════════════════════════════ */}
        <View
          fixed
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 70,          /* altura fija e invariable en todas las páginas */
            paddingHorizontal: 50,
            paddingTop: 6,
            borderTopWidth: 0.5,
            borderTopColor: COLOR_GRAY_MID,
          }}
        >
          {/* Fila superior: Código Único (siempre reserva el mismo alto) */}
          <View style={{ height: 14, justifyContent: "center" }}>
            <Text
              style={{ fontSize: 8, fontFamily: "Helvetica", fontStyle: "italic", color: COLOR_MUTED }}
              render={({ pageNumber, totalPages }) =>
                pageNumber === totalPages
                  ? `Código Único: ${formData?.codigo_unico || "N/A"}`
                  : ""
              }
            />
          </View>

          {/* Fila inferior: texto legal + número de página */}
          <View style={{ flexDirection: "row", alignItems: "flex-start" }}>
            <Text style={{ fontSize: 6.5, fontFamily: "Helvetica", color: COLOR_MUTED, flex: 1, lineHeight: 1.4, paddingRight: 10 }}>
             Toda impresión física de este documento se considera Documento no Controlado. La versión vigente se encuentra
            en el software para la administración de la planeación y la gestión. El registro de datos personales, autoriza
            a la entidad para la recolección, almacenamiento y uso de los mismos conforme a las disposiciones contenidas
            en la Ley 1581 de 2012 y las que modifique, adiciones o complementen. Conozca la Política de Privacidad y Tratamiento
            de Datos Personales a través de http://www.reincorporacion.gov.co/es/atencion/Paginas/Aviso-de-privacidad.aspx
            </Text>
            <Text
              style={{ fontSize: 7.5, fontFamily: "Helvetica-Bold", color: COLOR_MUTED, minWidth: 55, textAlign: "right" }}
              render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`}
            />
          </View>
        </View>

      </Page>
    </Document>
  );

};

const ActaFinalPDF = forwardRef(function ActaFinalPDF({ proyectoId, tipoFormatoID, onClose, codigo_interno }, ref) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);


useEffect(() => {
  // 🔥 No ejecutar si aún no existen los valores
  if (!proyectoId || !tipoFormatoID) return;

  const fetchProyecto = async () => {
    try {
      setCargando(true);

      const response = await getAllProyectoByid(
        proyectoId,
        tipoFormatoID
      );

      if (response?.success) {

        const data = response.data || {};
         setDatos(data);
        message.success("Datos cargados correctamente");

      } else {
        onClose();
        message.error("Ocurrió un error al obtener los datos");
      }

    } catch (error) {
      onClose();
      message.error("Ocurrió un error al obtener los datos");

    } finally {
      setCargando(false);
    }
  };

  fetchProyecto();

}, [proyectoId, tipoFormatoID]);


  /*
  useEffect(() => {
    if (!proyectoId) return;
    setCargando(true);
    fetch(`/api/sostenibilidad/proyectos/${proyectoId}`)
      .then((r) => r.json())
      .then((r) => setDatos(r.data))
      .finally(() => setCargando(false));
  }, [proyectoId]);

  */

  useImperativeHandle(ref, () => ({
    descargarPDF: async () => {
      if (!datos) throw new Error("Los datos aún no han cargado");
      const blob = await pdf(<ActaFinalDocumento formData={datos} />).toBlob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href      = url;
      a.download  = `acta_final_${codigo_interno}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    },
  }), [datos]);

  if (cargando) return <div style={{ padding: 32, textAlign: "center" }}>Cargando acta...</div>;
  if (!datos)   return <div style={{ padding: 32, textAlign: "center" }}>Sin datos</div>;

  return (
    <PDFViewer width="100%" height={700} showToolbar={false}>
      <ActaFinalDocumento formData={datos} />
    </PDFViewer>
  );
});

export default ActaFinalPDF;